/**
 * LooseRelationshipTargetPicker — top section of the FkLookupPicker shown when
 * a non-FK column has been given the `lookup` operator but no loose-FK target
 * is declared yet. Lets the user pick the target schema/table and column,
 * with an inferred suggestion when the heuristic finds one. Saving persists
 * the relationship via metaInfoApi; the parent picker re-renders with a real
 * target on the next MetaInfo invalidation.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { JsonPathField } from './JsonPathField';
import {
  findCandidateTables,
  pickTargetColumn,
  type InferTable,
  type InferSource,
} from '../../utils/inferLooseRelationship';
import {
  addEnumColumn,
  addLooseRelationship,
  removeLooseRelationship,
  updateLooseRelationship,
  upsertTableDisplayColumn,
  type LooseRelationship,
} from '../../services/metaInfoApi';
import { useInvalidateMetaInfo, useMetaInfo } from '../../hooks/useMetaInfo';
import { useLooseRelationshipPeek } from '../../hooks/useLooseRelationshipPeek';
import { refreshLookupCacheForColumn } from '../../utils/lookupCacheRefresh';
import { qualifyLookupTarget } from '../../utils/lookupEnumTarget';
import { useTableMetadata } from '../../../table-explorer/hooks/useTableMetadata';
import { useAnalytics } from '../../../../../core/analytics';
import type { DeclareEnumColumnDialogTrigger } from './declareEnumColumnDialogStore';
import type { FilterGroup, TableExplorerColumn } from '../../../core/types/workspaceTypes';
import { LooseRelationshipFilterPane } from './LooseRelationshipFilterPane';
import { LooseFkDisplayColumnSelect } from './LooseFkDisplayColumnSelect';
import { LooseFkTreatAsEnumField } from './LooseFkTreatAsEnumField';
import { LooseRelationshipPeek } from '../meta-info/LooseRelationshipPeek';
import { categorizeJsonColumn, isNativeJsonColumn } from '../../utils/jsonColumnCategorization';
import { normalizeJsonPath } from '../../utils/jsonFilterSql';
import { couldFiltersOverlap } from '../../utils/looseFkOverlap';
import { findTableDisplayColumn } from '../../utils/tableDisplayColumn';
import { pickDisplayColumn } from '../../../table-explorer/utils/pickDisplayColumn';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import { hostMetadataQueryKey, type TableExplorerMetadataEditorProviders } from '../../../table-explorer/components/tableExplorerHostPorts';
import { HostCandidateColumns, type HostCandidateColumn } from './HostCandidateColumns';

export type LooseRelationshipTargetPickerProps = {
  connectionId: string;
  database: string;
  source: InferSource;
  /** All tables in the database (schema + name; columns optional). */
  allTables: InferTable[];
  /** When set, we're editing an existing relationship — Save calls update, not add. */
  existingRelationshipId?: string;
  initialTarget?: { schema: string; table: string; column: string };
  initialSourceFilter?: FilterGroup[];
  initialTargetFilter?: FilterGroup[];
  /** When editing an existing JSON-pathed loose FK, the current sourcePath. */
  initialSourcePath?: string;
  /**
   * Engine of the connection — required to detect whether the source column
   * is JSON (native or declared) so we can show the optional `sourcePath`
   * input. When omitted, the JSON-path input is suppressed.
   */
  engine?: DatabaseEngineId;
  onSaved: (rel: LooseRelationship) => void;
  onCancel?: () => void;
  /**
   * Layout mode. `'horizontal'` (default) lays table + column + actions in a
   * single row — fine inside a wide dialog. `'stacked'` puts each on its own
   * row so the picker fits inside narrower hosts like a popover.
   */
  layout?: 'horizontal' | 'stacked';
  /**
   * When true, the picker fills its parent's height and splits into a
   * scrollable body + a pinned (non-scrolling) bottom action bar. The host
   * must give the picker a bounded height (e.g. a flex parent inside a
   * `max-h` dialog). Default `false` keeps the flat content-height layout
   * used by the FkLookupPicker declare-first flow.
   */
  scrollable?: boolean;
  /**
   * Entry point that triggered the add flow — propagated to the
   * `feature:loose_fk_added` analytics event so we can distinguish the
   * filter-`lookup` operator entry point ('inline', default) from
   * the FK popover footer's "+ Add another" entry point.
   */
  addedSource?: string;
  metadataEditors?: TableExplorerMetadataEditorProviders;
};

type SelectedTable = { schema: string; name: string };

/**
 * How many rows the referenced table may hold before "Also treat as enum"
 * stops being offered. A lookup enum materialises the target as a dropdown, so
 * past a couple of hundred rows the row picker (which the FK badge already
 * gives you) is the better tool. Matches the pair-fetch bound (`201`, i.e.
 * 200 + the truncation probe).
 */
const LOOKUP_ENUM_MAX_TARGET_ROWS = 200;

/** Ties the declare event fired below to the dialog's closed trigger set. */
const ENUM_DECLARE_TRIGGER: DeclareEnumColumnDialogTrigger = 'loose-fk-editor';

export function LooseRelationshipTargetPicker({
  connectionId,
  database,
  source,
  allTables,
  existingRelationshipId,
  initialTarget,
  initialSourceFilter,
  initialTargetFilter,
  initialSourcePath,
  engine,
  onSaved,
  onCancel,
  layout = 'horizontal',
  scrollable = false,
  addedSource = 'inline',
  metadataEditors,
}: LooseRelationshipTargetPickerProps) {
  // Phase 1 of inference: pure name-matching against the lightweight table list.
  // This works even when `allTables` carries no column data.
  const candidates = useMemo(
    () =>
      findCandidateTables(
        source,
        allTables.map(({ schema, name }) => ({ schema, name })),
      ),
    [source, allTables],
  );
  const topCandidate = candidates[0] ?? null;

  // Phase 2 of inference: hydrate the top candidate's metadata so we can pick
  // its target column (PK > id > type match). Reuses the existing hook so we
  // don't introduce a new IPC channel.
  const candidateMeta = useTableMetadata({
    connectionId,
    database,
    schema: topCandidate?.schema ?? '',
    tableName: topCandidate?.name ?? '',
    enabled: !!topCandidate && !initialTarget && !metadataEditors,
  });
  // Host providers: the candidate's columns come from `<HostCandidateColumns>`, rendered only for a host, so a SQL
  // picker registers no extra query observer (S4 review G, S1).
  const [providerColumns, setProviderColumns] = useState<HostCandidateColumn[]>();

  const inferredColumn = useMemo<string | null>(() => {
    if (!topCandidate || (!candidateMeta.metadata && !providerColumns)) return null;
    const cols = providerColumns ??
      candidateMeta.metadata?.columns?.map((c) => ({
        name: c.name,
        dataType: c.dataType ?? '',
      })) ?? [];
    const pk = providerColumns?.find((column) => column.isPrimaryKey)?.name
      ?? [...candidateMeta.primaryKeyColumns][0];
    return pickTargetColumn({ columns: cols, primaryKey: pk }, source.dataType);
  }, [topCandidate, candidateMeta.metadata, candidateMeta.primaryKeyColumns, providerColumns, source.dataType]);

  const inferred = useMemo(() => {
    if (!topCandidate || !inferredColumn) return null;
    return { schema: topCandidate.schema, table: topCandidate.name, column: inferredColumn };
  }, [topCandidate, inferredColumn]);

  const [targetTable, setTargetTable] = useState<SelectedTable | null>(
    initialTarget
      ? { schema: initialTarget.schema, name: initialTarget.table }
      : topCandidate
        ? { schema: topCandidate.schema, name: topCandidate.name }
        : null,
  );
  const [targetColumn, setTargetColumn] = useState<string | null>(
    initialTarget?.column ?? null,
  );
  const [sourcePath, setSourcePath] = useState<string>(initialSourcePath ?? '');
  const [userTouched, setUserTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sourceFilter, setSourceFilter] = useState<FilterGroup[]>(initialSourceFilter ?? []);
  const [targetFilter, setTargetFilter] = useState<FilterGroup[]>(initialTargetFilter ?? []);
  // Display column state. `undefined` = pristine (effect will pre-fill from
  // persisted state or the name heuristic); `null` = explicit "no label
  // column" (user picked `(none)` deliberately, or persisted entry resolved
  // to an absent column); `string` = the chosen column name. Tracked separately
  // from `userTouched` because target table changes should reset the heuristic
  // baseline for the label column too, but display-column edits shouldn't be
  // clobbered by a late table-metadata fetch.
  const [displayColumn, setDisplayColumn] = useState<string | null | undefined>(undefined);
  const [displayColumnTouched, setDisplayColumnTouched] = useState(false);
  // "Also treat as enum" — opt-in, never pre-checked: declaring an enum is a
  // second, independent fact about the column and must be the user's choice.
  const [treatAsEnum, setTreatAsEnum] = useState(false);

  // The optional sourcePath input is only meaningful when the source column
  // is JSON (native or declared) — otherwise the user has nothing to extract.
  // The peek popover's server-scan SQL needs `declaredOnText` to wrap the
  // column in the engine's JSON-validity guard, so split the category out.
  const { data: metaInfo } = useMetaInfo(connectionId, database);
  const sourceJsonCategory = useMemo(() => {
    if (!engine) return null;
    return categorizeJsonColumn(
      {
        engine,
        schema: source.schema,
        table: source.table,
        name: source.name,
        dataType: source.dataType,
      },
      metaInfo ?? null,
    );
  }, [engine, source, metaInfo]);
  const sourceIsJson =
    sourceJsonCategory === 'native-json' || sourceJsonCategory === 'declared-json';
  // Only declared-on-text needs the engine's JSON-validity guard wrap in the
  // peek popover's server-scan SQL; declared *native* JSON columns don't.
  const sourceDeclaredOnText =
    !!engine &&
    sourceJsonCategory === 'declared-json' &&
    !isNativeJsonColumn({
      engine,
      schema: source.schema,
      table: source.table,
      name: source.name,
      dataType: source.dataType,
    });

  // Once the candidate's metadata loads, pre-fill targetColumn (and confirm
  // targetTable) — but only when we're not editing an existing relationship
  // and the user hasn't already touched the table or column manually. This
  // prevents a late metadata resolution from clobbering the user's choice.
  useEffect(() => {
    if (initialTarget) return;
    if (userTouched) return;
    if (!inferred) return;
    if (targetColumn) return;
    setTargetTable({ schema: inferred.schema, name: inferred.table });
    setTargetColumn(inferred.column);
  }, [inferred, initialTarget, userTouched, targetColumn]);

  // The persisted "label column" for the current target table — keyed by
  // `(schema, table)` and shared across every loose FK targeting this table.
  // Falls back to `undefined` when no entry exists; the prefill effect below
  // then runs the heuristic instead.
  const persistedDisplayColumnEntry = useMemo(
    () =>
      targetTable
        ? findTableDisplayColumn(metaInfo ?? null, targetTable.schema, targetTable.name)
        : undefined,
    [metaInfo, targetTable],
  );

  // Reset the display-column dropdown when the user picks a different target
  // table. The prefill effect (after `columnsQuery` below) re-reads the
  // persisted entry / re-runs the heuristic against the new target table.
  const targetTableSchema = targetTable?.schema;
  const targetTableName = targetTable?.name;
  useEffect(() => {
    setDisplayColumn(undefined);
    setDisplayColumnTouched(false);
  }, [targetTableSchema, targetTableName]);

  const invalidate = useInvalidateMetaInfo();
  const { track } = useAnalytics();

  const tableOptions = useMemo(
    () =>
      allTables.map((t) => ({
        value: `${t.schema}.${t.name}`,
        label: `${t.schema}.${t.name}`,
      })),
    [allTables],
  );

  // Lazy-fetch column metadata for the chosen target table so we can populate
  // the column dropdown. allTables typically only carries schema + name (no
  // columns) since the project's "list tables" hooks don't return columns.
  // Host providers: under the host cache scope, in the `lookup-target-columns`
  // entry the other host surfaces share (S4 review F, F12); SQL key unchanged.
  const columnsQuery = useQuery({
    queryKey: metadataEditors
      ? hostMetadataQueryKey(metadataEditors, connectionId, 'lookup-target-columns', database, targetTable?.schema, targetTable?.name)
      : ['loose-fk-target-columns', connectionId, database, targetTable?.schema, targetTable?.name],
    queryFn: async () => {
      if (!targetTable) return [] as string[];
      // Prefer columns we already have on InferTable when present.
      const fromList = allTables.find(
        (t) => t.schema === targetTable.schema && t.name === targetTable.name,
      );
      if (fromList && fromList.columns.length > 0) {
        return fromList.columns.map((c) => c.name);
      }
      if (metadataEditors) {
        const columns = await metadataEditors.listColumns({
          connectionId,
          database,
          schema: targetTable.schema,
          table: targetTable.name,
        });
        return columns.map((column) => column.name);
      }
      if (!window.electronAPI?.getTableMetadata) return [] as string[];
      const result = await window.electronAPI.getTableMetadata({
        connectionId,
        database,
        schema: targetTable.schema,
        tableName: targetTable.name,
      });
      if (!result.success || !result.metadata) return [] as string[];
      return result.metadata.columns.map((c) => c.name);
    },
    enabled: !!targetTable,
    staleTime: 5 * 60 * 1000,
  });

  const columnOptions = useMemo(() => {
    const names = columnsQuery.data ?? [];
    return names.map((n) => ({ value: n, label: n }));
  }, [columnsQuery.data]);

  // Pre-fill the dropdown from MetaInfo's `tableDisplayColumns` first; fall
  // back to the name-based heuristic once columns load. Skipped after the
  // user has manually picked a value — the heuristic would otherwise overwrite
  // an intentional choice on the next re-render. The persisted entry is
  // applied even before columns finish loading (so the dropdown reflects
  // what's on disk without flicker).
  useEffect(() => {
    if (displayColumnTouched) return;
    if (!targetTable) return;
    if (persistedDisplayColumnEntry) {
      setDisplayColumn(
        persistedDisplayColumnEntry.column === ''
          ? null
          : persistedDisplayColumnEntry.column,
      );
      return;
    }
    const cols = columnsQuery.data ?? [];
    if (cols.length === 0) return;
    setDisplayColumn(pickDisplayColumn(cols, targetColumn ?? undefined));
  }, [
    persistedDisplayColumnEntry,
    columnsQuery.data,
    targetTable,
    targetColumn,
    displayColumnTouched,
  ]);

  // Render-time value for the SearchableSelect — the persisted sentinel is
  // `''` (column missing or explicit none) while local state uses `null`.
  // The `'__none__'` string mirrors the same sentinel inside
  // `LooseFkDisplayColumnSelect`; kept inline to avoid an extra export from
  // that component (which would trigger the react-refresh lint rule).
  const displayColumnSelectValue =
    displayColumn === null ? '__none__' : (displayColumn ?? '');

  // Host providers: under the host cache scope, so a reconnect or a mutation re-reads the source's properties
  // (S4 review F, F12); SQL key unchanged.
  const sourceColumnsQuery = useQuery({
    queryKey: metadataEditors
      ? hostMetadataQueryKey(metadataEditors, connectionId, 'source-columns', database, source.schema, source.table)
      : ['loose-fk-source-columns', connectionId, database, source.schema, source.table],
    queryFn: async () => {
      if (metadataEditors) {
        return metadataEditors.listColumns({
          connectionId,
          database,
          schema: source.schema,
          table: source.table,
        });
      }
      if (!window.electronAPI?.getTableMetadata) return [] as Array<{ name: string; dataType: string }>;
      const result = await window.electronAPI.getTableMetadata({
        connectionId,
        database,
        schema: source.schema,
        tableName: source.table,
      });
      if (!result.success || !result.metadata) return [];
      return result.metadata.columns.map((c) => ({ name: c.name, dataType: c.dataType ?? '' }));
    },
    staleTime: 5 * 60 * 1000,
  });

  const sourceColumns: TableExplorerColumn[] = useMemo(
    () =>
      (sourceColumnsQuery.data ?? []).map((c) => ({
        name: c.name,
        dataType: c.dataType,
        nullable: false,
        isPrimaryKey: false,
        isForeignKey: false,
      })),
    [sourceColumnsQuery.data],
  );

  const targetColumnsForFilter: TableExplorerColumn[] = useMemo(
    () =>
      (columnsQuery.data ?? []).map((name) => ({
        name,
        dataType: '',
        nullable: false,
        isPrimaryKey: false,
        isForeignKey: false,
      })),
    [columnsQuery.data],
  );

  // Normalize whitespace-only / blank sourcePath input to undefined so we
  // never persist a sentinel that would route through `extractTextSql` for a
  // non-JSON FK. `normalizeJsonPath('')` returns `'$'` which we explicitly
  // treat as "no path declared" — extracting from `$` is the document itself.
  const normalizedSourcePath = useMemo<string | undefined>(() => {
    if (!sourceIsJson) return undefined;
    const trimmed = sourcePath.trim();
    if (trimmed === '' || trimmed === '$') return undefined;
    return normalizeJsonPath(trimmed);
  }, [sourcePath, sourceIsJson]);

  // --- "Also treat as enum" (lookup enum in the same save) -----------------
  //
  // The offer is only honest when the referenced table is small enough to
  // enumerate, so it rides on the peek pane's live TARGET count — the same
  // hook, deliberately subscribed WITHOUT the label column and WITHOUT the
  // target filter:
  //   - the lookup fetch ignores `targetFilter` (design doc), so the count
  //     that decides this is the UNFILTERED one — what the pairs query would
  //     actually see;
  //   - the label column resolves a beat after the target does, so including
  //     it would change the query key mid-form and blink the checkbox out
  //     while the second fetch is in flight.
  // The narrower key stays put for as long as the target does.
  const peek = useLooseRelationshipPeek({
    connectionId,
    database,
    source: { schema: source.schema, table: source.table },
    target: {
      schema: targetTable?.schema ?? '',
      table: targetTable?.name ?? '',
      keyColumn: targetColumn ?? '',
    },
    enabled: !!targetTable && !!targetColumn && !metadataEditors,
  });

  // A column can hold exactly one enum declaration, whatever its source — an
  // existing one (sampled, native, another lookup) means this checkbox has
  // nothing to add and would only race the one already there.
  const sourceHasEnumDeclaration = useMemo(
    () =>
      (metaInfo?.enumColumns ?? []).some(
        (decl) =>
          decl.schema === source.schema &&
          decl.table === source.table &&
          decl.column === source.name,
      ),
    [metaInfo, source.schema, source.table, source.name],
  );

  // `0` doubles as "not known yet": the hook reports `0` before its first
  // result lands, and a target with no rows has nothing to enumerate anyway.
  // During a REFETCH the previous count is still there, so the checkbox holds
  // still instead of blinking out while the user edits a target filter.
  const targetRowCount = peek.target.error ? 0 : peek.target.count;

  // Deliberately absent — never disabled — when the count is unknown, too
  // large, or the draft isn't a plain unconditional column reference: a
  // greyed-out control the user can't act on is just noise in a form this
  // dense. A source filter (polymorphic dispatch) or a JSON `sourcePath` both
  // mean the target depends on the row, which no single enum can express
  // (`resolveLookupEnumTarget`'s V1 rules).
  const canTreatAsEnum =
    !!engine &&
    !!targetTable &&
    !!targetColumn &&
    targetRowCount > 0 &&
    targetRowCount <= LOOKUP_ENUM_MAX_TARGET_ROWS &&
    sourceFilter.length === 0 &&
    !normalizedSourcePath &&
    !sourceHasEnumDeclaration;

  const queryClient = useQueryClient();

  /**
   * Persist the lookup-enum declaration for a relationship that just saved,
   * then warm its pair cache immediately (forced past the throttle) so labels
   * are on screen when the dialog closes — the miss-driven self-heal would
   * only reach them after the user hit an unknown key.
   */
  const declareLookupEnum = async (relationshipId: string) => {
    if (!engine || !targetTable || !targetColumn) return;
    const lookup = {
      toSchema: targetTable.schema,
      toTable: targetTable.name,
      toColumn: targetColumn,
      relationshipId,
    };
    await addEnumColumn(connectionId, database, {
      id: crypto.randomUUID(),
      schema: source.schema,
      table: source.table,
      column: source.name,
      source: 'lookup',
      createdAt: new Date().toISOString(),
      lookup,
    });
    track('feature:loose_fk_enum_checkbox', { engine, checked: true });
    track('feature:enum_declare', {
      engine,
      source: 'lookup',
      trigger: ENUM_DECLARE_TRIGGER,
      on_json_path: false,
    });
    // Background — the save must not wait on the target table.
    void refreshLookupCacheForColumn({
      queryClient,
      connectionId,
      database,
      engine,
      schema: source.schema,
      table: source.table,
      column: source.name,
      target: lookup,
      getMetaInfo: () => metaInfo,
      reason: 'declare',
      force: true,
      track,
      invalidate,
      metadataEditors,
    });
  };

  const onDelete = async () => {
    if (!existingRelationshipId) return;
    const confirmed = window.confirm(
      "Remove this loose-FK declaration? This won't delete data — only the relationship.",
    );
    if (!confirmed) return;
    setDeleting(true);
    setError(null);
    try {
      await removeLooseRelationship(connectionId, database, existingRelationshipId);
      await invalidate(connectionId, database);
      track('feature:loose_fk_removed', { source: 'editor_dialog' });
      // Hosts treat onCancel as "close / exit edit mode" — same effect we
      // want after a delete since the relationship no longer exists.
      onCancel?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeleting(false);
    }
  };

  const onSave = async () => {
    if (!targetTable || !targetColumn) return;

    // Overlap guard — when there's another declaration on the same source
    // triple whose source filter could match the same rows as the one
    // we're about to save, the popover would show overlapping tabs on
    // those rows. The polymorphic-dispatch pattern is the intended use;
    // overlap is almost always a misconfiguration. We warn before save.
    //
    // Scope: the check applies only to declarations that share the same
    // `sourcePath`. Two loose FKs on the same JSON column with different
    // JSON paths (e.g. `$.user_id` and `$.org_id`) target different
    // logical sub-keys and are not overlapping — they're addressed by
    // `resolveActiveLooseRel(sourcePath)` at lookup time. We normalize
    // `undefined` and `''` to the same "no path" bucket.
    const myPath = normalizedSourcePath ?? '';
    const others = (metaInfo?.looseRelationships ?? []).filter(
      (r) =>
        r.fromSchema === source.schema &&
        r.fromTable === source.table &&
        r.fromColumn === source.name &&
        r.id !== existingRelationshipId &&
        (r.sourcePath ?? '') === myPath,
    );
    const overlapping = others.filter((r) =>
      couldFiltersOverlap(
        sourceFilter.length > 0 ? sourceFilter : undefined,
        r.sourceFilter,
      ),
    );
    if (overlapping.length > 0) {
      const confirmed = window.confirm(
        `Another loose FK on ${source.schema}.${source.table}.${source.name} could match the same rows as this one — both would activate together on overlapping rows (multi-tab popover).\n\nFor polymorphic dispatch (e.g. commentable_type = 'Post' vs 'Photo'), give each declaration a source filter that picks a different subset.\n\nSave anyway?`,
      );
      track('feature:loose_fk_overlap_warning', {
        action: confirmed ? 'confirmed' : 'cancelled',
        mode: existingRelationshipId ? 'edit' : 'add',
      });
      if (!confirmed) return;
    }

    setSaving(true);
    setError(null);
    try {
      // Persist the table-level display column when the user changed it. The
      // entry is keyed by `(targetSchema, targetTable)` so future loose FKs
      // pointing at the same table inherit the same label automatically.
      // Skipped when the user never touched the dropdown — the prefilled
      // heuristic value isn't a deliberate choice and shouldn't burn a write.
      if (displayColumnTouched && targetTable) {
        const persistedValue = persistedDisplayColumnEntry?.column;
        const nextValue = displayColumn === null ? '' : (displayColumn ?? '');
        if (persistedValue !== nextValue) {
          await upsertTableDisplayColumn(connectionId, database, {
            schema: targetTable.schema,
            table: targetTable.name,
            column: nextValue,
            createdAt: new Date().toISOString(),
          });
          track('feature:loose_fk_display_column_set', {
            value: nextValue === '' ? 'cleared' : 'set',
            source: existingRelationshipId ? 'edit' : 'create',
          });
        }
      }

      // Only a VISIBLE checkbox can declare — the state survives the rule
      // going false (a filter added, a path typed) and must not act then.
      const alsoDeclareEnum = canTreatAsEnum && treatAsEnum;

      if (existingRelationshipId) {
        const updated = await updateLooseRelationship(
          connectionId,
          database,
          existingRelationshipId,
          {
            toSchema: targetTable.schema,
            toTable: targetTable.name,
            toColumn: targetColumn,
            sourceFilter: sourceFilter.length > 0 ? sourceFilter : undefined,
            targetFilter: targetFilter.length > 0 ? targetFilter : undefined,
            sourcePath: normalizedSourcePath,
          },
        );
        track('feature:loose_fk_edited', {
          has_path: !!normalizedSourcePath,
        });
        if (alsoDeclareEnum) await declareLookupEnum(existingRelationshipId);
        await invalidate(connectionId, database);
        const rel = updated.looseRelationships.find(
          (r) => r.id === existingRelationshipId,
        );
        if (rel) onSaved(rel);
      } else {
        const rel: LooseRelationship = {
          id: crypto.randomUUID(),
          fromSchema: source.schema,
          fromTable: source.table,
          fromColumn: source.name,
          toSchema: targetTable.schema,
          toTable: targetTable.name,
          toColumn: targetColumn,
          sourceFilter: sourceFilter.length > 0 ? sourceFilter : undefined,
          targetFilter: targetFilter.length > 0 ? targetFilter : undefined,
          ...(normalizedSourcePath ? { sourcePath: normalizedSourcePath } : {}),
          createdAt: new Date().toISOString(),
        };
        await addLooseRelationship(connectionId, database, rel);
        track('feature:loose_fk_declare', {
          has_path: !!normalizedSourcePath,
        });
        track('feature:loose_fk_added', { source: addedSource });
        if (alsoDeclareEnum) await declareLookupEnum(rel.id);
        await invalidate(connectionId, database);
        onSaved(rel);
      }
      const sourcePrev = (initialSourceFilter ?? []).length > 0;
      const sourceNext = sourceFilter.length > 0;
      const targetPrev = (initialTargetFilter ?? []).length > 0;
      const targetNext = targetFilter.length > 0;
      if (!sourcePrev && sourceNext) track('feature:loose_fk_filter_added', { side: 'source' });
      if (sourcePrev && !sourceNext) track('feature:loose_fk_filter_removed', { side: 'source' });
      if (!targetPrev && targetNext) track('feature:loose_fk_filter_added', { side: 'target' });
      if (targetPrev && !targetNext) track('feature:loose_fk_filter_removed', { side: 'target' });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const tableValue = targetTable ? `${targetTable.schema}.${targetTable.name}` : '';

  // Action-bar buttons are identical across flat and scrollable shells; only
  // the wrapper (padding / border / sticky) differs. Stacked layout keeps its
  // own inline buttons (rendered inside formBody), so this is horizontal-only.
  const actionBarContent =
    layout !== 'stacked' ? (
      <>
        {existingRelationshipId && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={onDelete}
            disabled={saving || deleting}
            data-testid="loose-fk-target-delete"
            title="Remove this loose-FK declaration"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" />
            {deleting ? 'Removing…' : 'Delete'}
          </Button>
        )}
        <div className="flex-1" />
        {onCancel && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={onCancel}
            disabled={saving || deleting}
          >
            Cancel
          </Button>
        )}
        <Button
          type="button"
          size="sm"
          onClick={onSave}
          disabled={!targetTable || !targetColumn || saving || deleting}
          data-testid="loose-fk-target-save"
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </>
    ) : null;

  const formBody = (
    <>
      {metadataEditors && topCandidate && !initialTarget && (
        <HostCandidateColumns metadataEditors={metadataEditors} connectionId={connectionId} database={database}
          schema={topCandidate.schema} table={topCandidate.name} onColumns={setProviderColumns} />
      )}
      {layout === 'stacked' ? (
        <div className="flex flex-col gap-2">
          <SearchableSelect
            value={tableValue}
            onValueChange={(v) => {
              const dotIdx = v.indexOf('.');
              if (dotIdx <= 0) return;
              const schema = v.slice(0, dotIdx);
              const name = v.slice(dotIdx + 1);
              setTargetTable({ schema, name });
              setTargetColumn(null);
              setUserTouched(true);
            }}
            options={tableOptions}
            placeholder="schema.table"
            data-testid="loose-fk-target-table"
          />
          <SearchableSelect
            value={targetColumn ?? ''}
            onValueChange={(v) => {
              setTargetColumn(v);
              setUserTouched(true);
            }}
            options={columnOptions}
            placeholder={
              !targetTable
                ? 'pick table first'
                : columnsQuery.isLoading
                  ? 'loading…'
                  : 'column'
            }
            disabled={!targetTable || columnsQuery.isLoading}
            data-testid="loose-fk-target-column"
          />
          <div className="flex items-center justify-end gap-2">
            {onCancel && (
              <Button size="sm" variant="ghost" onClick={onCancel} disabled={saving}>
                Cancel
              </Button>
            )}
            <Button
              size="sm"
              onClick={onSave}
              disabled={!targetTable || !targetColumn || saving}
              data-testid="loose-fk-target-save"
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      ) : (
        <>
          {/* Pickers row — Target table | Referenced column | Label column.
              Each dropdown has a label above; the Label-column slot occupies
              the space the Save/Cancel buttons used to fill (those moved to
              the bottom action bar — see end of the form). */}
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-[180px] flex flex-col gap-1">
              <label
                htmlFor="loose-fk-target-table"
                className="text-[11px] text-muted-foreground"
              >
                Target table
              </label>
              <SearchableSelect
                value={tableValue}
                onValueChange={(v) => {
                  const dotIdx = v.indexOf('.');
                  if (dotIdx <= 0) return;
                  const schema = v.slice(0, dotIdx);
                  const name = v.slice(dotIdx + 1);
                  setTargetTable({ schema, name });
                  setTargetColumn(null);
                  setUserTouched(true);
                }}
                options={tableOptions}
                placeholder="schema.table"
                portal={scrollable}
                data-testid="loose-fk-target-table"
              />
            </div>
            <div className="w-44 flex flex-col gap-1">
              <label
                htmlFor="loose-fk-target-column"
                className="text-[11px] text-muted-foreground"
              >
                Referenced column
              </label>
              <SearchableSelect
                value={targetColumn ?? ''}
                onValueChange={(v) => {
                  setTargetColumn(v);
                  setUserTouched(true);
                }}
                options={columnOptions}
                placeholder={
                  !targetTable
                    ? 'pick table first'
                    : columnsQuery.isLoading
                      ? 'loading…'
                      : 'column'
                }
                disabled={!targetTable || columnsQuery.isLoading}
                portal={scrollable}
                data-testid="loose-fk-target-column"
              />
            </div>
            {targetTable && targetColumn && (
              <div className="w-56">
                <LooseFkDisplayColumnSelect
                  value={displayColumnSelectValue}
                  onChange={(next) => {
                    setDisplayColumn(next === '__none__' ? null : next);
                    setDisplayColumnTouched(true);
                  }}
                  columnOptions={columnOptions}
                  loading={columnsQuery.isLoading}
                  targetSchema={targetTable.schema}
                  targetTable={targetTable.name}
                  // A loose FK navigates fine with no label column — here it
                  // genuinely is optional, unlike in the lookup flows.
                  optional
                  portal={scrollable}
                />
              </div>
            )}
          </div>
        </>
      )}
      {/* One save, two facts: the relationship AND (optionally) the enum
          declaration that renders its labels everywhere. Only offered when the
          referenced table is small enough to enumerate — see `canTreatAsEnum`. */}
      {canTreatAsEnum && targetTable && (
        <LooseFkTreatAsEnumField
          checked={treatAsEnum}
          onCheckedChange={setTreatAsEnum}
          disabled={saving || deleting}
          targetLabel={qualifyLookupTarget(targetTable.schema, targetTable.name)}
          targetRowCount={targetRowCount}
        />
      )}
      {sourceIsJson && engine && (
        <div
          className="flex flex-col gap-1"
          data-testid="loose-fk-source-path-row"
        >
          <label className="text-[11px] text-muted-foreground" htmlFor="loose-fk-source-path">
            JSON path inside <span className="font-mono">{source.name}</span>{' '}
            <span className="text-muted-foreground/70">(optional)</span>
          </label>
          <JsonPathField
            triggerId="loose-fk-source-path"
            connectionId={connectionId}
            database={database}
            schema={source.schema}
            table={source.table}
            column={source.name}
            engine={engine}
            declaredOnText={sourceDeclaredOnText}
            value={sourcePath}
            onChange={setSourcePath}
            disabled={saving}
            showWildcardHint={false}
            triggerTestId="loose-fk-source-path-peek"
            inputTestId="loose-fk-source-path-input"
          />
          <p className="text-[10px] text-muted-foreground/80 leading-snug">
            When set, the FK lookup compares the value at this JSON path
            (e.g. <span className="font-mono">$.customer_id</span>) against the
            target column. Leave blank to compare the whole column.
          </p>
        </div>
      )}
      {targetTable && targetColumn && (
        <div className="flex flex-col gap-2 mt-2">
          <LooseRelationshipFilterPane
            side="source"
            filters={sourceFilter}
            onFiltersChange={setSourceFilter}
            columns={sourceColumns}
            connectionId={connectionId}
            database={database}
            tableSchema={source.schema}
            tableName={source.table}
          />
          <LooseRelationshipFilterPane
            side="target"
            filters={targetFilter}
            onFiltersChange={setTargetFilter}
            columns={targetColumnsForFilter}
            connectionId={connectionId}
            database={database}
            tableSchema={targetTable.schema}
            tableName={targetTable.name}
          />
          {/* The row-count peek runs SQL (`fetchTableExplorerRows`); a host with
              injected metadata providers (Azure Tables) has no SQL path, so it is
              not rendered there — the same gate the `peek` hook above uses. */}
          {!metadataEditors && <LooseRelationshipPeek
            connectionId={connectionId}
            database={database}
            source={{ schema: source.schema, table: source.table, filter: sourceFilter }}
            target={{
              schema: targetTable.schema,
              table: targetTable.name,
              keyColumn: targetColumn,
              displayColumn:
                displayColumn && displayColumn !== '' ? displayColumn : undefined,
              filter: targetFilter,
            }}
          />}
        </div>
      )}
      {inferred && !initialTarget && (
        <div
          className="text-[11px] text-muted-foreground"
          data-testid="loose-fk-target-suggestion"
        >
          Suggested:{' '}
          <span className="font-mono">
            {inferred.schema}.{inferred.table}.{inferred.column}
          </span>
        </div>
      )}
      {error && (
        <div className="text-[11px] text-destructive" data-testid="loose-fk-target-error">
          {error}
        </div>
      )}
    </>
  );

  // Scrollable shell — sticky bottom action bar, the form body scrolls when
  // the host (a bounded-height dialog) runs out of vertical space. The host
  // is responsible for giving this a flex parent + max-height; here we just
  // fill it and split body/footer.
  if (scrollable) {
    return (
      <div
        className="flex flex-col flex-1 min-h-0"
        data-testid="loose-fk-target-picker"
      >
        <div className="flex flex-col gap-2 flex-1 min-h-0 overflow-y-auto px-6 py-4">
          {formBody}
        </div>
        {actionBarContent && (
          <div
            // The fill is a MASK, not a band: this bar's job is to be opaque
            // so the scrolled form cannot bleed through it, while still
            // reading as part of the host dialog — `border-t` is the only
            // thing meant to separate them. So it tracks `bg-surface-raised`,
            // what DialogContent paints (CH-22), not `bg-background`. Both
            // call sites are dialogs (FkLookupPicker, LooseRelationshipEditorDialog);
            // if this picker is ever hosted on the app canvas, this needs to
            // follow the new host.
            className="shrink-0 flex items-center gap-2 px-6 py-3 border-t border-border bg-surface-raised"
            data-testid="loose-fk-target-actions"
          >
            {actionBarContent}
          </div>
        )}
      </div>
    );
  }

  // Flat shell — content-height layout used by the FkLookupPicker
  // declare-first flow (host doesn't bound the height).
  return (
    <div
      className="flex flex-col gap-2 p-1"
      data-testid="loose-fk-target-picker"
    >
      {formBody}
      {actionBarContent && (
        <div
          className="flex items-center gap-2 pt-2 mt-1 border-t border-border"
          data-testid="loose-fk-target-actions"
        >
          {actionBarContent}
        </div>
      )}
    </div>
  );
}
